import { Helmet } from "react-helmet-async";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Services = () => {
  return (
    <>
      <Helmet>
        <title>Services - Agro-Réfrigération</title>
        <meta 
          name="description" 
          content="Découvrez nos services sur mesure pour optimiser votre production laitière : assistance à la traite, détection de fuites, inspection, rapports Pro Action et urgence 24/7." 
        />
      </Helmet>

      <div className="space-y-12">
        <div className="text-center space-y-4">
          <p className="text-sm uppercase tracking-wider text-accent font-semibold">Services sur mesure</p>
          <h1 className="text-3xl md:text-4xl md:text-5xl font-bold">Des services adaptés à vos besoins</h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Chez Agro, nous comprenons que chaque ferme laitière a des besoins spécifiques. C'est pourquoi 
            nous offrons une gamme de services sur mesure, conçus pour optimiser votre production laitière 
            et assurer le bien-être de vos vaches.
          </p>
        </div>

        <Tabs defaultValue="assistance" className="w-full">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-5 h-auto">
            <TabsTrigger value="assistance" className="py-3">Assistance à la traite</TabsTrigger>
            <TabsTrigger value="fuites" className="py-3">Détection de fuites</TabsTrigger>
            <TabsTrigger value="inspection" className="py-3">Inspection</TabsTrigger>
            <TabsTrigger value="proaction" className="py-3">Rapports Pro Action</TabsTrigger>
            <TabsTrigger value="urgence" className="py-3">Urgence 24/7</TabsTrigger>
          </TabsList>

          <TabsContent value="assistance" className="mt-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Assistance à la traite</CardTitle>
                <CardDescription className="text-base">
                  Optimisez votre système de traite et améliorez la qualité du lait
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg overflow-hidden">
                  <img 
                    src="https://agroref.ca/wp-content/uploads/2023/07/Agroref_lely-2-1024x681.jpg"
                    alt="Assistance à la traite"
                    className="w-full h-auto"
                  />
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Un service qui vise à optimiser votre système de traite, améliorer la qualité du lait 
                  et le bien-être de vos vaches. Notre expertise s'appuie sur des années d'expérience dans 
                  le soutien des fermes laitières. En plus de maintenir des conditions sanitaires optimales, 
                  nous fournissons une inspection du système de traite pour détecter rapidement les problèmes 
                  et y remédier.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Nos équipes techniques sont à votre disposition pour une formation efficace et pour garantir 
                  la salubrité du lait produit. Avec Agro, bénéficiez d'un service d'assistance à la traite 
                  exceptionnel et contribuez à un meilleur bien-être pour vos vaches laitières.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="fuites" className="mt-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Détection et réparation de fuites</CardTitle>
                <CardDescription className="text-base">
                  Technologies avancées pour localiser et réparer les fuites
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  Les fuites dans les systèmes de refroidissement sont un problème récurrent qui peut impacter 
                  l'efficacité et la durée de vie de vos équipements. Chez Agro, nous employons des technologies 
                  avancées pour localiser rapidement ces fuites.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Notre approche consiste à travailler étroitement avec nos clients pour concevoir une solution 
                  de réparation adaptée à leurs besoins spécifiques. Le résultat ? Une efficacité énergétique 
                  améliorée, une durée de vie prolongée de votre système, et des économies sur les coûts de 
                  réparation à long terme.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="inspection" className="mt-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Inspection du système de traite</CardTitle>
                <CardDescription className="text-base">
                  Évaluation complète pour garantir la qualité du lait
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  La qualité du lait est déterminée par une multitude de facteurs, notamment le système de traite. 
                  Chez Agro, nous comprenons l'importance d'un système de traite performant et fiable pour la 
                  qualité du lait.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Notre service d'inspection du système de traite, réalisé par des experts qualifiés, assure une 
                  évaluation complète pour détecter tout problème potentiel. Le résultat est un lait de qualité 
                  supérieure, une réduction des risques de maladie pour vos vaches laitières, et une diminution 
                  des coûts de maintenance.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="proaction" className="mt-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Rapports Pro Action</CardTitle>
                <CardDescription className="text-base">
                  Analyses détaillées pour optimiser votre production
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  Le moindre détail compte en production laitière. Les rapports Pro Action reflètent l'état de 
                  santé de votre ferme, fournissant une analyse de la composition du lait et des informations 
                  sur le bien-être de vos vaches.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Notre service de rapports Pro Action déchiffre ces données complexes pour vous. Grâce à nos 
                  analyses de laboratoire rigoureuses et à nos recommandations personnalisées, optimisez votre 
                  productivité et rentabilité.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="urgence" className="mt-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Urgence 24/7 – Une assistance rapide et fiable en tout temps</CardTitle>
                <CardDescription className="text-base">
                  Disponible jour et nuit pour vos urgences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  Faites face aux imprévus sans stress grâce à notre Service d'urgence 24/7. Disponible même 
                  en dehors des heures d'ouverture traditionnelles, notre équipe d'experts est toujours prête 
                  à intervenir. Dans les situations critiques où chaque minute compte, nous nous engageons à 
                  fournir une réponse rapide et efficace.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Notre Service d'urgence 24/7 offre une assistance constante, jour et nuit. Il comprend un 
                  support téléphonique en temps réel et des interventions sur site si nécessaire. Diagnostics 
                  et résolutions rapides vous permettent de reprendre vos activités sans tarder.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
};

export default Services;
